import { useEffect, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { getGymMemberId, getMembershipSerialNo, isPendingGymMemberId } from '../../pages/admin/members/memberData';
import { dedupeMembers } from '../../utils/dedupeList';

function normalizeId(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, '');
}

function memberPhone(member) {
  return String(member?.phone || member?.mobile || '').trim();
}

function memberCnic(member) {
  return String(member?.cnic || member?.cnicNo || member?.profile?.cnic || '').trim();
}

function memberMatchesQuery(member, query) {
  const raw = query.trim().toLowerCase();
  if (!raw) return false;

  const serial = normalizeId(getGymMemberId(member) || getMembershipSerialNo(member));
  const queryNorm = normalizeId(raw.replace(/^id[\s\-:_]*/i, ''));
  const queryDigits = raw.replace(/\D/g, '');
  const name = (member.name || '').toLowerCase();
  const serialDigits = serial.replace(/\D/g, '');
  const phone = memberPhone(member).toLowerCase();
  const phoneDigits = phone.replace(/\D/g, '');
  const cnic = memberCnic(member).toLowerCase();
  const cnicDigits = cnic.replace(/\D/g, '');

  return (
    (serial && (
      serial.includes(normalizeId(raw))
      || (queryNorm && serial.includes(queryNorm))
      || (queryDigits && serialDigits.includes(queryDigits))
    ))
    || name.includes(raw)
    || (phone && (
      phone.includes(raw)
      || (queryDigits.length >= 3 && phoneDigits.includes(queryDigits))
    ))
    || (cnic && (
      cnic.includes(raw)
      || normalizeId(cnic).includes(normalizeId(raw))
      || (queryDigits.length >= 4 && cnicDigits.includes(queryDigits))
    ))
  );
}

function resolveDisplayId(member) {
  const fromHelper = getGymMemberId(member);
  if (fromHelper && !isPendingGymMemberId(fromHelper)) return fromHelper;
  return '—';
}

function MatchList({ members, onSelect, emptyHint }) {
  return (
    <div className="rec-gym-id-matches" role="listbox" aria-label="Matching members">
      {members.length > 0 ? (
        members.map((member) => {
          const displayId = resolveDisplayId(member);
          const phone = memberPhone(member);
          const cnic = memberCnic(member);
          return (
            <button
              key={member.id}
              type="button"
              role="option"
              aria-selected={false}
              className="rec-gym-id-match"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(member)}
            >
              <span className="rec-gym-id-match-id">{displayId}</span>
              <span className="rec-gym-id-match-name">{member.name || '—'}</span>
              {phone ? <span className="rec-gym-id-match-phone">{phone}</span> : null}
              {cnic ? <span className="rec-gym-id-match-phone">{cnic}</span> : null}
            </button>
          );
        })
      ) : (
        <p className="rec-gym-id-empty">{emptyHint}</p>
      )}
    </div>
  );
}

function GymIdMemberSearch({
  id,
  label,
  query,
  onQueryChange,
  selectedName = '',
  selectedGymId = '',
  selectedMember = null,
  onSelect,
  members,
  placeholder = 'Search serial no, name, phone, or CNIC...',
  className = '',
  /** When true, `members` are already filtered by the server — still client-filter by needle. */
  serverFiltered = false,
  /** Allow editing serial / name after a member is selected. */
  editableSelection = false,
}) {
  const [listOpen, setListOpen] = useState(false);
  /** Local drafts so typing serial/name always drives search even before parent re-renders */
  const [draftSerial, setDraftSerial] = useState(selectedGymId || '');
  const [draftName, setDraftName] = useState(selectedName || '');
  /** Which edit field is driving the related search: 'serial' | 'name' | null */
  const [editSearchField, setEditSearchField] = useState(null);

  const locked = Boolean(selectedMember);

  // Keep drafts in sync when a member is chosen (or cleared from outside).
  // Skip while the user is actively typing in an edit field so keystrokes are not overwritten.
  useEffect(() => {
    if (editSearchField) return;
    setDraftSerial(isPendingGymMemberId(selectedGymId) ? '' : (selectedGymId || ''));
    setDraftName(selectedName || '');
    if (selectedMember) setEditSearchField(null);
  }, [selectedMember, selectedGymId, selectedName, editSearchField]);

  const searchNeedle = editSearchField === 'serial'
    ? String(draftSerial || '')
    : editSearchField === 'name'
      ? String(draftName || '')
      : String(query || '');

  const matches = useMemo(() => {
    const q = searchNeedle.trim();
    if (!q || !listOpen) return [];
    // Locked selection with no active edit → hide dropdown
    if (locked && !editSearchField) return [];
    const list = dedupeMembers(members);
    // Always filter by what the user typed (server results are only a candidate pool)
    return list.filter((member) => memberMatchesQuery(member, q));
  }, [members, searchNeedle, locked, listOpen, editSearchField]);

  const handleQueryChange = (value) => {
    onQueryChange(value);
    setEditSearchField(null);
    setDraftSerial('');
    setDraftName('');
    setListOpen(Boolean(String(value || '').trim()));
    if (selectedName || selectedGymId || selectedMember) {
      onSelect({ name: '', gymId: '', member: null });
    }
  };

  const handleSelect = (member) => {
    const gymId = resolveDisplayId(member);
    const cleanId = gymId === '—' ? '' : gymId;
    const name = member.name || '';
    setListOpen(false);
    setEditSearchField(null);
    setDraftSerial(cleanId);
    setDraftName(name);
    onSelect({ name, gymId: cleanId, member });
    onQueryChange(cleanId || name);
  };

  const startEditSearch = (field, value) => {
    const text = String(value || '');
    if (field === 'serial') {
      setDraftSerial(text);
      setDraftName(selectedName || draftName);
    } else {
      setDraftName(text);
      setDraftSerial(selectedGymId || draftSerial);
    }

    const serialUnchanged = text.trim() === String(selectedGymId || '').trim();
    const nameUnchanged = text.trim() === String(selectedName || '').trim();
    const stillSameSelection = Boolean(selectedMember) && (
      (field === 'serial' && serialUnchanged)
      || (field === 'name' && nameUnchanged)
    );

    // Keep the selected member until the user actually changes serial/name
    if (!stillSameSelection) {
      onSelect({
        name: field === 'name' ? text : (selectedName || draftName || ''),
        gymId: field === 'serial' ? text : (selectedGymId || draftSerial || ''),
        member: null,
      });
      onQueryChange(text);
    }
    setEditSearchField(field);
    setListOpen(Boolean(text.trim()));
  };

  const showMainMatches = Boolean(query.trim()) && !locked && !editSearchField && listOpen;
  const showEditMatches = Boolean(editSearchField) && listOpen && Boolean(searchNeedle.trim());
  const showEditFields = editableSelection && (locked || editSearchField || draftSerial || draftName);
  const emptyHint = 'No members match this serial no, name, phone, or CNIC.';

  // serverFiltered is kept for callers; results are always client-filtered by needle
  void serverFiltered;

  return (
    <div className={className}>
      {label && (
        <label className="owner-form-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="rec-member-search-field rec-gym-id-search-field">
        <MdSearch className="rec-member-search-icon" />
        <input
          id={id}
          type="text"
          className="owner-form-input rec-member-search-input"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => {
            if (!locked && query.trim()) {
              setEditSearchField(null);
              setListOpen(true);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>

      {showMainMatches && (
        <MatchList members={matches} onSelect={handleSelect} emptyHint={emptyHint} />
      )}

      {showEditFields ? (
        <div className="rec-gym-id-selected-edit" style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <div className="owner-form-group" style={{ margin: 0 }}>
            <label className="owner-form-label" htmlFor={`${id}-edit-serial`}>Serial No</label>
            <input
              id={`${id}-edit-serial`}
              type="text"
              className="owner-form-input"
              value={draftSerial}
              onChange={(e) => startEditSearch('serial', e.target.value)}
              onFocus={() => {
                // Open match list only — do NOT clear selected member on focus
                setEditSearchField('serial');
                if (String(draftSerial || selectedGymId || '').trim()) setListOpen(true);
              }}
              placeholder="Type serial to search related members"
              autoComplete="off"
            />
          </div>
          {showEditMatches && editSearchField === 'serial' ? (
            <MatchList members={matches} onSelect={handleSelect} emptyHint={emptyHint} />
          ) : null}

          <div className="owner-form-group" style={{ margin: 0 }}>
            <label className="owner-form-label" htmlFor={`${id}-edit-name`}>Member name</label>
            <input
              id={`${id}-edit-name`}
              type="text"
              className="owner-form-input"
              value={draftName}
              onChange={(e) => startEditSearch('name', e.target.value)}
              onFocus={() => {
                // Open match list only — do NOT clear selected member on focus
                setEditSearchField('name');
                if (String(draftName || selectedName || '').trim()) setListOpen(true);
              }}
              placeholder="Type name to search related members"
              autoComplete="off"
            />
          </div>
          {showEditMatches && editSearchField === 'name' ? (
            <MatchList members={matches} onSelect={handleSelect} emptyHint={emptyHint} />
          ) : null}
        </div>
      ) : selectedName && !editableSelection ? (
        <p className="rec-gym-id-selected">
          Selected: <strong>{selectedName}</strong>
          {selectedGymId && !isPendingGymMemberId(selectedGymId) ? ` (${selectedGymId})` : ''}
        </p>
      ) : null}
    </div>
  );
}

export default GymIdMemberSearch;
